import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BookOpen, Search } from 'lucide-react'
import { useMinCopy } from '@/i18n/useUiLocale'
import { ArticleCardSkeleton } from '@/components/ArticleCardSkeleton'
import { EmptyState } from '@/components/EmptyState'
import { ErrorScreen } from '@/components/ErrorScreen'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { ArticleCard } from '@/components/reading/ArticleCard'
import { FilterTriggerButton } from '@/components/mobile/FilterTriggerButton'
import { MobileBottomSheet, SheetOptionButton } from '@/components/mobile/MobileBottomSheet'
import { articlesApi, type ArticleReadStatus } from '@/api/articles'
import type { CefrLevel } from '@/types/common'
import { cn } from '@/components/ui/cn'

const CEFR_TABS: Array<{ value: CefrLevel | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'A1', label: 'A1' },
  { value: 'A2', label: 'A2' },
  { value: 'B1', label: 'B1' },
  { value: 'B2', label: 'B2' },
  { value: 'C1', label: 'C1' },
  { value: 'C2', label: 'C2' },
]

const PAGE_SIZE = 12

interface ArticleLibrarySectionProps {
  search: string
  cefrLevel: CefrLevel | ''
  activeTag: string
  readStatus: ArticleReadStatus
  page: number
  onSearchChange: (value: string) => void
  onCefrChange: (level: CefrLevel | '') => void
  onTagChange: (tag: string) => void
  onReadStatusChange: (status: ArticleReadStatus) => void
  onPageChange: (page: number) => void
}

export function ArticleLibrarySection({
  search,
  cefrLevel,
  activeTag,
  readStatus,
  page,
  onSearchChange,
  onCefrChange,
  onTagChange,
  onReadStatusChange,
  onPageChange,
}: ArticleLibrarySectionProps) {
  const { reading } = useMinCopy()
  const READ_STATUS_TABS: Array<{ value: ArticleReadStatus; label: string }> = [
    { value: 'ALL', label: reading.filterAll },
    { value: 'UNREAD', label: reading.filterUnread },
    { value: 'READ', label: reading.filterRead },
  ]

  const [readSheetOpen, setReadSheetOpen] = useState(false)
  const [cefrSheetOpen, setCefrSheetOpen] = useState(false)
  const [tagSheetOpen, setTagSheetOpen] = useState(false)

  const hasActiveFilters = Boolean(search || cefrLevel || activeTag || readStatus !== 'ALL')

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['articles', search, cefrLevel, activeTag, readStatus, page],
    queryFn: () =>
      articlesApi
        .getArticles({
          search: search || undefined,
          cefrLevel: cefrLevel || undefined,
          tag: activeTag || undefined,
          readStatus: readStatus === 'ALL' ? undefined : readStatus,
          page,
          size: PAGE_SIZE,
        })
        .then((r) => r.data),
  })

  const { data: tags } = useQuery({
    queryKey: ['article-tags'],
    queryFn: () => articlesApi.getTags().then((r) => r.data),
  })

  const articles = data?.content ?? []
  const totalPages = data?.totalPages ?? 0

  const activeReadLabel = READ_STATUS_TABS.find((t) => t.value === readStatus)?.label ?? 'All'
  const activeCefrLabel = CEFR_TABS.find((t) => t.value === cefrLevel)?.label ?? 'All levels'
  const activeTagLabel = activeTag ? activeTag : 'All topics'

  const handleSearchInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(e.target.value)
      onPageChange(0)
    },
    [onSearchChange, onPageChange]
  )

  const selectReadStatus = (value: ArticleReadStatus) => {
    onReadStatusChange(value)
    onPageChange(0)
    setReadSheetOpen(false)
  }

  const selectCefr = (value: CefrLevel | '') => {
    onCefrChange(value)
    onPageChange(0)
    setCefrSheetOpen(false)
  }

  const selectTag = (tag: string) => {
    onTagChange(tag)
    onPageChange(0)
    setTagSheetOpen(false)
  }

  return (
    <section data-testid="reading-library-section">
      <div className="sticky top-0 z-10 -mx-6 mb-5 border-b border-border/40 bg-background/95 px-6 pb-3 pt-1 backdrop-blur-sm">
        <div className="mb-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search articles…"
              value={search}
              onChange={handleSearchInput}
              className="pl-9"
            />
          </div>
        </div>

        {/* Mobile filter triggers */}
        <div className="flex flex-wrap gap-2 lg:hidden" data-testid="reading-mobile-filters">
          <FilterTriggerButton
            label={activeReadLabel}
            onClick={() => setReadSheetOpen(true)}
            testId="reading-filter-read-status"
          />
          <FilterTriggerButton
            label={activeCefrLabel}
            onClick={() => setCefrSheetOpen(true)}
            testId="reading-filter-cefr"
          />
          {tags && tags.length > 0 ? (
            <FilterTriggerButton
              label={activeTagLabel}
              onClick={() => setTagSheetOpen(true)}
              testId="reading-filter-tag"
            />
          ) : null}
        </div>

        {/* Desktop filters */}
        <div className="hidden lg:block">
          <div className="mb-2.5 flex gap-2">
            <div
              className="flex shrink-0 rounded-lg border border-border/60 bg-muted/20 p-0.5"
              data-testid="reading-read-status-filter"
            >
              {READ_STATUS_TABS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    onReadStatusChange(value)
                    onPageChange(0)
                  }}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    readStatus === value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex shrink-0 items-center gap-1">
              {CEFR_TABS.map(({ value, label }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    onCefrChange(value)
                    onPageChange(0)
                  }}
                  className={cn(
                    'rounded-full px-3 py-1 text-sm font-medium transition-colors',
                    cefrLevel === value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {tags && tags.length > 0 && (
              <>
                <div className="h-4 w-px shrink-0 bg-border/60" />
                <div className="relative min-w-0 flex-1">
                  <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-6 bg-gradient-to-r from-background to-transparent" />
                  <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-6 bg-gradient-to-l from-background to-transparent" />
                  <div className="flex gap-1.5 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {tags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => {
                          onTagChange(activeTag === tag ? '' : tag)
                          onPageChange(0)
                        }}
                        className={cn(
                          'shrink-0 rounded-full border px-3 py-0.5 text-xs font-medium capitalize transition-colors',
                          activeTag === tag
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:border-primary/40'
                        )}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <ArticleCardSkeleton />
      ) : isError ? (
        <ErrorScreen
          variant="network"
          title="Couldn't load articles"
          description="Please check your connection and try again."
          onRetry={() => refetch()}
        />
      ) : articles.length === 0 ? (
        <EmptyState
          icon={<BookOpen className="h-12 w-12" />}
          title={hasActiveFilters ? 'No articles found' : 'Start reading'}
          description={
            hasActiveFilters
              ? 'Try changing filters or search terms.'
              : 'Browse articles at your level to discover new vocabulary in context.'
          }
          action={
            hasActiveFilters ? (
              <Button
                className="w-auto"
                onClick={() => {
                  onSearchChange('')
                  onCefrChange('')
                  onTagChange('')
                  onReadStatusChange('ALL')
                  onPageChange(0)
                }}
              >
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>

          {totalPages > 1 ? (
            <div className="mt-6 flex items-center justify-center gap-2">
              <Button
                className="w-auto px-4 border border-border bg-background text-foreground hover:bg-muted"
                disabled={page === 0}
                onClick={() => onPageChange(page - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                {page + 1} / {totalPages}
              </span>
              <Button
                className="w-auto px-4 border border-border bg-background text-foreground hover:bg-muted"
                disabled={page >= totalPages - 1}
                onClick={() => onPageChange(page + 1)}
              >
                Next
              </Button>
            </div>
          ) : null}
        </>
      )}

      <MobileBottomSheet
        open={readSheetOpen}
        title="Read status"
        onClose={() => setReadSheetOpen(false)}
        testId="reading-read-status-sheet"
      >
        {READ_STATUS_TABS.map(({ value, label }) => (
          <SheetOptionButton
            key={value}
            active={readStatus === value}
            label={label}
            onClick={() => selectReadStatus(value)}
          />
        ))}
      </MobileBottomSheet>

      <MobileBottomSheet
        open={cefrSheetOpen}
        title="Level"
        onClose={() => setCefrSheetOpen(false)}
        testId="reading-cefr-sheet"
      >
        {CEFR_TABS.map(({ value, label }) => (
          <SheetOptionButton
            key={label}
            active={cefrLevel === value}
            label={label === 'All' ? 'All levels' : label}
            onClick={() => selectCefr(value)}
          />
        ))}
      </MobileBottomSheet>

      {tags && tags.length > 0 ? (
        <MobileBottomSheet
          open={tagSheetOpen}
          title="Topic"
          onClose={() => setTagSheetOpen(false)}
          testId="reading-tag-sheet"
        >
          <SheetOptionButton active={!activeTag} label="All topics" onClick={() => selectTag('')} />
          {tags.map((tag) => (
            <SheetOptionButton
              key={tag}
              active={activeTag === tag}
              label={tag}
              onClick={() => selectTag(activeTag === tag ? '' : tag)}
            />
          ))}
        </MobileBottomSheet>
      ) : null}
    </section>
  )
}
